import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import ReactDOM from "react-dom";
import {FormattedMessage, useIntl} from "react-intl";

import {GlobalContext} from "../../globals/globalContext.jsx";
import {calculateNextDeliveryDate, formatDeliveryDateIntl, parseRRULE,} from "../../util/helpers.js";
import AmountSelector from "./AmountSelector.jsx";
import DeliveryInfo from "./DeliveryInfo.jsx";
import ModalFooter from "./ModalFooter.jsx";
import ModalHeader from "./ModalHeader.jsx";
import OptionSelector from "./OptionSelector.jsx";
import "./styles.styl";
import {useRuleInitialiser} from "./useRuleInitialiser.js";
import ToggleSwitch from "../switch/ToggleSwitch";
import DateSelectionList from "../date-selection-list/DateSelectionList";
import {useDateSelection} from "./useDateSelection";

export default function ItemOptionsModal({
  deliver_order,
  deliver_listing,
  delivery,
  onClose,
  onConfirm,
  rule,
  quantityInCart,
  fromCatalog,
  fromSettings,
  fromSettingsRemoveRule,
  isUnavailable,
}) {
  const intl = useIntl();
  const ONCE = "ONCE";

  const { logoSmallUrl, subscription } = useContext(GlobalContext);
  const modalRef = useRef();

  const [amount, setAmount] = useState(1);
  const [initialAmount, setInitialAmount] = useState(0);
  const [salePrice, setSalePrice] = useState("");
  const [periodicity, setPeriodicity] = useState("");
  const [amountError, setAmountError] = useState("");
  const [periodicityError, setPeriodicityError] = useState("");
  const [isAmountSelectorDisabled, setIsAmountSelectorDisabled] =
    useState(false);

  const [deliveryDateString, setDeliveryDateString] = useState("");
  const { options, setOptions, parsedRule } = useRuleInitialiser(
    rule,
    setAmount,
    setInitialAmount,
    setPeriodicity,
    deliver_listing,
    deliver_order,
    intl,
    fromSettings,
    fromSettingsRemoveRule,
  );

  const [syncWithCart, setSyncWithCart] = useState(false);
  const shouldDisplayToggle =
    !fromSettings &&
    periodicity !== ONCE &&
    // amount !== initialAmount &&
    amount !== quantityInCart;

  const deliveryIsSoldAndNotEdited =
    periodicity !== ONCE && delivery.sold && !delivery.sold_and_edited;
  const hasNoRuleOrRuleIsNotTemporary = !rule || !rule?.is_temporary;
  const hasRuleAndRuleIsNotTemporary = rule && !rule?.is_temporary;

  const isAdjustingForFutureDate =
    fromSettings ||
    (!fromCatalog &&
      !syncWithCart &&
      ((shouldDisplayToggle &&
        (hasRuleAndRuleIsNotTemporary || deliveryIsSoldAndNotEdited)) ||
        (deliveryIsSoldAndNotEdited && hasNoRuleOrRuleIsNotTemporary)));

  const [dates, setDates] = useState(
    Array(4).fill({ date: "", original_date: "", selected: false }),
  );
  const [isLoadingUpcomingDates, setIsLoadingUpcomingDates] = useState(false);
  const [shakeDateIndex, setShakeDateIndex] = useState(null);
  const [dateSelectionMessage, setDateSelectionMessage] = useState("");
  const [datesError, setDatesError] = useState("");

  const nextDeliveryDate = useMemo(() => {
    if (periodicity !== ONCE && dates.every((dateInfo) => dateInfo.date === "" && !dateInfo.selected)) {
      return {};
    }

    const currentDate = delivery.delivery_date.split(".").reverse().join("-");

    const rule = periodicity === ONCE
      ? parseRRULE("RRULE:FREQ=WEEKLY;INTERVAL=1")
      : parseRRULE(periodicity);

    const { timeframeShort, formattedDate } = calculateNextDeliveryDate(
      currentDate,
      rule,
      delivery.timeframe_short,
      periodicity === ONCE ? [] : dates,
      intl,
    );

    return { timeframeShort, formattedDate };
  }, [delivery.delivery_date, periodicity, isAdjustingForFutureDate, dates]);

  const currentDeliveryDate = useMemo(() => {
    const currentDate = delivery.delivery_date.split(".").reverse().join("-");
    const deliveryDate = new Date(currentDate);

    const { timeframeShort, formattedDate } = formatDeliveryDateIntl(
      deliveryDate,
      delivery.timeframe_short,
      intl,
    );

    return { timeframeShort, formattedDate };
  }, [delivery.delivery_date]);

  const confirmButtonLabel = isAdjustingForFutureDate
    ? intl.formatMessage(
        { id: "adjustOrderForDate" },
        { date: nextDeliveryDate.formattedDate },
      )
    : intl.formatMessage({ id: "confirm" });

  useEffect(() => {
    const formatPrice = (price) => {
      return price
        ? intl.formatNumber(price, {
            style: "currency",
            currency: "EUR",
          })
        : "";
    };

    if (fromSettings) {
      setOptions((prevOptions) => {
        const updatedOptions = prevOptions.map((option) => ({
          ...option,
          disabled:
            (option.value === ONCE &&
              !fromSettingsRemoveRule &&
              rule === undefined) ||
            (isUnavailable && option.value !== ONCE),
        }));

        if (rule === undefined && updatedOptions.length > 0) {
          if (!deliver_listing.default_recurrence && !periodicity) {
            setPeriodicity(updatedOptions[1].value);
            fetchUpcomingDates(updatedOptions[1].value);
          } else if (deliver_listing.default_recurrence && periodicity) {
            fetchUpcomingDates(periodicity);
          }
        } else if (fromSettingsRemoveRule && !periodicity) {
          setPeriodicity(updatedOptions[0].value);
          fetchUpcomingDates(updatedOptions[0].value);
        }

        return updatedOptions;
      });
    }

    let _salePrice;
    if (deliver_order) {
      _salePrice = formatPrice(
        deliver_order.sale_price_pro_bu !==
          deliver_order.sale_price_pro_sale_unit
          ? deliver_order.sale_price_pro_sale_unit
          : deliver_order.sale_price_pro_bu,
      );
    } else {
      _salePrice = formatPrice(
        deliver_listing.sale_price_pro_bu !==
          deliver_listing.sale_price_pro_sale_unit
          ? deliver_listing.sale_price_pro_sale_unit
          : deliver_listing.sale_price_pro_bu,
      );
    }

    setSalePrice(_salePrice);
    setDeliveryDateString(
      `${currentDeliveryDate.timeframeShort}, ${currentDeliveryDate.formattedDate}`,
    );
  }, [
    deliver_order,
    deliver_listing,
    delivery,
    periodicity,
    rule,
    parsedRule,
    intl,
  ]);

  useEffect(() => {
    if (rule) {
      setAmount(rule.temporary_amount);
    }
  }, [rule]);

  const handleDateClick = (dateObj) => {
    const date = dateObj.date;

    setDates((prevDates) => {
      const clickedDateIndex = prevDates.findIndex(
        (dateInfo) => dateInfo.date === date,
      );

      const deliveryDate = delivery.delivery_date;
      const isDisabled = prevDates[clickedDateIndex].disabled;
      const isSold = prevDates[clickedDateIndex].sold;

      const wasSelected = prevDates[clickedDateIndex].selected;
      const hasSameDateAsDeliveryAndIsGoingToBeDeselected =
        wasSelected && !fromSettings && deliveryDate === date;

      if (
        isDisabled ||
        isSold ||
        hasSameDateAsDeliveryAndIsGoingToBeDeselected
      ) {
        if (hasSameDateAsDeliveryAndIsGoingToBeDeselected && !isSold) {
          setShakeDateIndex(clickedDateIndex);
          setDateSelectionMessage(
            intl.formatMessage({ id: "cannotDeselectSameDeliveryDate" }),
          );
          setTimeout(() => setDateSelectionMessage(""), 5000);
        }

        return prevDates;
      }

      const newDates = prevDates.map((dateInfo) =>
        dateInfo.date === date
          ? { ...dateInfo, selected: !dateInfo.selected }
          : dateInfo,
      );

      setDateSelectionMessage("");

      return newDates;
    });
  };

  const { fetchUpcomingDates } = useDateSelection({
    delivery,
    rule,
    fromSettings,
    periodicity,
    dates,
    setDates,
    setIsLoadingUpcomingDates,
  });

  const periodicityChangedBySelection = useRef(false);
  const onPeriodicityChange = (selectedOption) => {
    setIsAmountSelectorDisabled(
      selectedOption.label === intl.formatMessage({ id: "noLongerDeliver" }),
    );
    periodicityChangedBySelection.current = true;
    setPeriodicity(selectedOption.value);
  };

  useEffect(() => {
    if (!periodicity) return;

    if (!delivery) return;

    const isInitialLoad = (fromCatalog || !fromSettings) && !rule;

    if (delivery || rule || isInitialLoad) {
      if (periodicity !== 'ONCE') {
        if (rule && !periodicityChangedBySelection.current) {
          fetchUpcomingDates();
        } else {
          fetchUpcomingDates(periodicity);
        }
      }
      periodicityChangedBySelection.current = false;
    }
  }, [periodicity, rule, delivery, fromCatalog, fromSettings]);

  useEffect(() => {
    const hasZeroAmount = deliver_order?.num_portions_in_cart === 0;
    if ((isAdjustingForFutureDate || hasZeroAmount) && periodicity) {
      setDeliveryDateString(
        `${nextDeliveryDate.timeframeShort}, ${nextDeliveryDate.formattedDate}`,
      );
    } else {
      setDeliveryDateString(
        `${currentDeliveryDate.timeframeShort}, ${currentDeliveryDate.formattedDate}`,
      );
    }
  }, [deliver_order, isAdjustingForFutureDate, periodicity, nextDeliveryDate]);

  const handleConfirm = () => {
    if (!validateInputs()) return;

    const firstSelectedDateInfo = dates.find((dateInfo) => dateInfo.selected);

    let dtStartString;
    let year, month, day;
    if (periodicity !== "ONCE") {
      if (firstSelectedDateInfo) {
        [year, month, day] =
          firstSelectedDateInfo.original_date_iso_formatted.split("-");
        dtStartString = `DTSTART:${year}${month}${day}T121212Z\n`;
      }
      if (!firstSelectedDateInfo) {
        // If no date is selected, use the next delivery date
        [year, month, day] = nextDeliveryDate.formattedDate.split("-");
        dtStartString = `DTSTART:${year}${month}${day}T121212Z\n`;
      }
    }

    let amountIncrement;

    if (!rule?.is_temporary && syncWithCart) {
      amountIncrement = amount - quantityInCart;
    } else {
      amountIncrement = amount - initialAmount;
    }
    const datesWithSelection = dates.map((dateInfo) => ({
      date: dateInfo.original_date_iso_formatted,
      selected: dateInfo.selected,
      calendarweek: dateInfo.calendarweek,
      iso_weekday: dateInfo.iso_weekday,
    }));
    const ruleProductData = {
      totalAmount: amount,
      amount: amountIncrement,
      periodicity:
        periodicity !== "ONCE" ? dtStartString + periodicity : periodicity,
      subscription: subscription.id,
      listing: deliver_listing.id,
      mode: "subscribe",
      is_temporary: rule ? rule.is_temporary : true,
      rule_id: rule ? rule.id : undefined,
      dates_with_selection: datesWithSelection,
      delivery_id: delivery.id,
    };
    const isStable = rule?.amount === rule?.temporary_amount;

    onConfirm(
      ruleProductData,
      isAdjustingForFutureDate,
      shouldDisplayToggle || isStable ? syncWithCart : undefined,
    );
  };

  const validateInputs = () => {
    let isValid = true;
    if (!periodicity) {
      setPeriodicityError(intl.formatMessage({ id: "periodicityError" }));
      isValid = false;
    } else {
      setPeriodicityError("");
    }

    if (!amount || amount < 1) {
      setAmountError(intl.formatMessage({ id: "amountError" }));
      isValid = false;
    } else {
      setAmountError("");
    }
    // Validation for date selection when there's no rule
    if (!rule && periodicity !== "ONCE") {
      const atLeastOneDateSelected = dates.some((date) => date.selected);
      if (!atLeastOneDateSelected) {
        setDatesError(intl.formatMessage({ id: "datesSelectionError" }));
        isValid = false;
      } else {
        setDatesError("");
      }
    } else {
      setDatesError("");
    }

    return isValid;
  };

  return ReactDOM.createPortal(
    <div className="modal-backdrop">
      <div className="modal" ref={modalRef}>
        <ModalHeader
          product_image={
            deliver_listing.image || deliver_listing?.article?.image
          }
          logoSmallUrl={logoSmallUrl}
          onClose={onClose}
          fromCatalog={fromCatalog}
        />

        <div className="modal-content">
          <div style={{ marginBottom: 10 }}>
            <FormattedMessage
              id="howManyUnits"
              values={{ b: (chunks) => <b>{chunks}</b> }}
            />
          </div>

          <AmountSelector
            amount={amount}
            setAmount={setAmount}
            salePrice={salePrice}
            deliver_listing={deliver_listing}
            amountError={amountError}
            disabled={isAmountSelectorDisabled || isUnavailable}
          />

          {shouldDisplayToggle && (
            <div
              className={`sync-toggle ${
                rule === undefined || rule?.is_temporary
                  ? "sync-toggle-disabled"
                  : ""
              }`}
            >
              <label>
                <FormattedMessage id="syncWithCartLabel" />
              </label>
              <ToggleSwitch
                name="syncWithCart"
                checked={
                  rule === null ||
                  rule?.is_temporary ||
                  (!rule && deliver_listing?.default_recurrence) ||
                  syncWithCart
                }
                handleCheckBox={() => setSyncWithCart(!syncWithCart)}
                disabled={
                  rule === null ||
                  rule?.is_temporary ||
                  (!rule && deliver_listing?.default_recurrence)
                }
              />
            </div>
          )}

          <div style={{ marginBottom: 10, marginTop: 10 }}>
            <FormattedMessage
              id="howOftenDelivery"
              values={{ b: (chunks) => <b>{chunks}</b> }}
            />
          </div>

          <OptionSelector
            options={options}
            periodicity={periodicity}
            setPeriodicity={(option) => {
              setPeriodicity(option.value);
              onPeriodicityChange(option);
            }}
            periodicityError={periodicityError}
          />

          {periodicity !== "ONCE" && (
            <>
              <FormattedMessage
                id="nextDeliveries"
                values={{ b: (chunks) => <b>{chunks}</b> }}
              />

              <DateSelectionList
                dates={dates}
                handleDateClick={handleDateClick}
                dateSelectionMessage={dateSelectionMessage}
                datesError={datesError}
                isLoadingUpcomingDates={isLoadingUpcomingDates}
                shakeDateIndex={shakeDateIndex}
                onShakeEnd={() => setShakeDateIndex(null)}
              />
            </>
          )}
        </div>

        <DeliveryInfo
          deliveryDateString={deliveryDateString}
          periodicity={periodicity}
          rule={rule}
        />

        <ModalFooter
          handleConfirm={handleConfirm}
          confirmButtonLabel={confirmButtonLabel}
          disabled={isLoadingUpcomingDates}
        />
      </div>
    </div>,
    document.body,
  );
}
